import { __esm, __export } from './chunk-NFEGQTCC.mjs';
import { MediaRecorder, register } from 'extendable-media-recorder';
import { connect } from 'extendable-media-recorder-wav-encoder';
import PCMPlayer from 'pcm-player';
import * as base64js from 'base64-js';
import { EventEmitter } from 'eventemitter3';

// src/chat/uimessage-stream-parser.ts
async function parseUIMessageStream(body, onChunk, onPart) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let lineBuffer = "";
  let fullText = "";
  const parts = [];
  const toolCalls = [];
  const openTextBlocks = /* @__PURE__ */ new Map();
  const toolParts = /* @__PURE__ */ new Map();
  let suppressed = false;
  let error = null;
  let signalError = null;
  let suppression;
  let handoff;
  let escalation;
  const emitPart = (part) => {
    parts.push(part);
    onPart?.(part);
  };
  const refuse = (signalType, detail) => {
    if (signalError === null) {
      signalError = `Refused a ${signalType} stream signal \u2014 ${detail}. This widget speaks stream-signal v${SUPPORTED_SIGNAL_VERSION}.`;
    }
  };
  const ensureToolPart = (toolCallId, toolName) => {
    let idx = toolParts.get(toolCallId);
    if (idx === void 0) {
      idx = parts.length;
      toolParts.set(toolCallId, idx);
      parts.push({
        type: "tool-invocation",
        toolInvocation: { toolCallId, toolName: toolName ?? "unknown", state: "call" }
      });
    } else if (toolName && parts[idx]?.toolInvocation) {
      parts[idx].toolInvocation.toolName = toolName;
    }
    return parts[idx]?.toolInvocation;
  };
  const handleSignal = (chunk, signalType) => {
    const data = chunk.data;
    if (!isRecord(data)) {
      refuse(signalType, "its payload is not an object");
      return;
    }
    const version = data.v;
    if (version !== void 0 && version !== SUPPORTED_SIGNAL_VERSION) {
      refuse(signalType, `it is tagged contract version ${JSON.stringify(version)}`);
      return;
    }
    if (signalType === STREAM_SIGNAL_TYPES.suppression) {
      if (data.reason !== "human_handler") {
        refuse(signalType, "its `reason` is outside the contracted enum");
        return;
      }
      suppressed = true;
      suppression = {
        reason: "human_handler",
        ...asString(data.threadId) ? { threadId: data.threadId } : {},
        ...asString(data.agentId) ? { agentId: data.agentId } : {}
      };
      emitPart({ type: "handoff" });
      return;
    }
    if (signalType === STREAM_SIGNAL_TYPES.handoff) {
      const origin = data.origin;
      if (origin !== "rep_takeover" && origin !== "ai_escalated") {
        refuse(signalType, "its `origin` is outside the contracted enum");
        return;
      }
      handoff = {
        origin,
        ...asString(data.reason) ? { reason: data.reason } : {},
        ...asString(data.threadId) ? { threadId: data.threadId } : {},
        ...asString(data.assigneeId) ? { assigneeId: data.assigneeId } : {},
        ...asString(data.repName) ? { repName: data.repName } : {}
      };
      emitPart({ type: "handoff" });
      return;
    }
    if (typeof data.escalated !== "boolean") {
      refuse(signalType, "its `escalated` verdict is missing or not a boolean");
      return;
    }
    escalation = {
      escalated: data.escalated,
      ...asString(data.reason) ? { reason: data.reason } : {},
      ...asString(data.transitionMessage) ? { transitionMessage: data.transitionMessage } : {}
    };
  };
  const handleChunk = (chunk) => {
    if (!isRecord(chunk)) return;
    const type = chunk.type;
    if (typeof type !== "string") return;
    switch (type) {
      case "text-delta": {
        const delta = chunk.delta;
        if (typeof delta !== "string" || delta.length === 0) return;
        fullText += delta;
        onChunk(delta);
        const id = typeof chunk.id === "string" ? chunk.id : "";
        const idx = openTextBlocks.get(id);
        if (idx !== void 0 && parts[idx]?.type === "text") {
          parts[idx].text = (parts[idx].text ?? "") + delta;
        } else {
          openTextBlocks.set(id, parts.length);
          parts.push({ type: "text", text: delta });
        }
        return;
      }
      case "text-start":
        return;
      case "text-end": {
        const id = typeof chunk.id === "string" ? chunk.id : "";
        openTextBlocks.delete(id);
        return;
      }
      case "error": {
        const msg = asString(chunk.errorText) ?? "Something went wrong. Please try again.";
        error = msg;
        emitPart({ type: "error", error: msg });
        return;
      }
      case "tool-input-start": {
        const toolCallId = asString(chunk.toolCallId);
        if (!toolCallId) return;
        ensureToolPart(toolCallId, asString(chunk.toolName));
        const idx = toolParts.get(toolCallId);
        onPart?.(parts[idx]);
        return;
      }
      case "tool-input-delta":
        return;
      case "tool-input-available": {
        const toolCallId = asString(chunk.toolCallId);
        if (!toolCallId) return;
        const invocation = ensureToolPart(toolCallId, asString(chunk.toolName));
        if (invocation && isRecord(chunk.input)) invocation.args = chunk.input;
        onPart?.({ ...parts[toolParts.get(toolCallId)] });
        return;
      }
      case "tool-output-available": {
        const toolCallId = asString(chunk.toolCallId);
        if (!toolCallId) return;
        const invocation = ensureToolPart(toolCallId);
        if (!invocation) return;
        invocation.state = "result";
        invocation.result = chunk.output;
        onPart?.({ ...parts[toolParts.get(toolCallId)] });
        toolCalls.push({
          toolName: invocation.toolName,
          args: invocation.args,
          result: chunk.output
        });
        return;
      }
      default:
        if (type === STREAM_SIGNAL_TYPES.suppression || type === STREAM_SIGNAL_TYPES.handoff || type === STREAM_SIGNAL_TYPES.escalation) {
          handleSignal(chunk, type);
          return;
        }
        return;
    }
  };
  const processLine = (rawLine) => {
    const line = rawLine.endsWith("\r") ? rawLine.slice(0, -1) : rawLine;
    if (!line) return;
    if (!line.startsWith("data:")) return;
    const payload = line.slice(5).trim();
    if (!payload || payload === "[DONE]") return;
    try {
      handleChunk(JSON.parse(payload));
    } catch {
    }
  };
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      if (!chunk) continue;
      lineBuffer += chunk;
      const lines = lineBuffer.split("\n");
      lineBuffer = lines.pop() ?? "";
      for (const line of lines) processLine(line);
    }
    if (lineBuffer) processLine(lineBuffer);
  } finally {
    reader.releaseLock();
  }
  return {
    text: fullText,
    parts,
    toolCalls,
    suppressed,
    error,
    signalError,
    ...suppression ? { suppression } : {},
    ...handoff ? { handoff } : {},
    ...escalation ? { escalation } : {}
  };
}
var STREAM_SIGNAL_TYPES, SUPPORTED_SIGNAL_VERSION, isRecord, asString;
var init_uimessage_stream_parser = __esm({
  "src/chat/uimessage-stream-parser.ts"() {
    STREAM_SIGNAL_TYPES = {
      suppression: "data-suppression",
      handoff: "data-handoff",
      escalation: "data-escalation"
    };
    SUPPORTED_SIGNAL_VERSION = 1;
    isRecord = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
    asString = (v) => typeof v === "string" && v.length > 0 ? v : void 0;
  }
});

// src/voice/utils.ts
var blobToBase64;
var init_utils = __esm({
  "src/voice/utils.ts"() {
    blobToBase64 = async (blob) => {
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result?.toString().split(",")[1] || null);
        };
        reader.readAsDataURL(blob);
      });
    };
  }
});
var AudioRecorder;
var init_audio_recorder = __esm({
  "src/voice/audio-recorder.ts"() {
    init_utils();
    AudioRecorder = class {
      constructor({
        trackConstraints = { echoCancellation: true },
        timeSlice = 10,
        onData,
        onStateChange = () => {
        },
        onError = (e) => console.error("[AudioRecorder]", e)
      }) {
        this.recorder = null;
        this.stream = null;
        this.config = {
          trackConstraints,
          timeSlice,
          onData,
          onStateChange,
          onError
        };
      }
      /**
       * Initialize the WAV encoder and start recording
       */
      async start() {
        try {
          if (!MediaRecorder.isTypeSupported("audio/wav")) {
            console.debug("[AudioRecorder] Registering WAV encoder");
            await register(await connect());
          }
          this.stream = await navigator.mediaDevices.getUserMedia({
            audio: this.config.trackConstraints
          });
          const micSettings = this.stream?.getAudioTracks()[0].getSettings();
          console.debug("[AudioRecorder] Mic settings:", micSettings);
          this.recorder = new MediaRecorder(this.stream, {
            mimeType: "audio/wav"
          });
          this.recorder.ondataavailable = this.handleDataAvailable.bind(this);
          this.recorder.onstop = this.handleStop.bind(this);
          this.recorder.onerror = (event) => this.config.onError(new Error(event.error.message));
          this.recorder.addEventListener("statechange", () => this.config.onStateChange(this.recorder?.state ?? "inactive"));
          this.recorder.start(this.config.timeSlice);
          console.debug("[AudioRecorder] Started recording");
        } catch (error) {
          this.config.onError(error instanceof Error ? error : new Error("Failed to start recording"));
          throw error;
        }
      }
      /**
       * Stop recording and cleanup resources
       */
      stop() {
        if (!this.recorder) return;
        try {
          if (this.recorder.state === "recording") {
            this.recorder.stop();
          }
          this.cleanup();
          console.debug("[AudioRecorder] Stopped recording");
        } catch (error) {
          this.config.onError(error instanceof Error ? error : new Error("Failed to stop recording"));
        }
      }
      /**
       * Get current recording state
       */
      getState() {
        return this.recorder?.state ?? "inactive";
      }
      /**
       * Check if currently recording
       */
      isRecording() {
        return this.recorder?.state === "recording";
      }
      async handleDataAvailable(event) {
        if (!event.data.size) return;
        try {
          const base64 = await blobToBase64(event.data);
          if (base64) {
            this.config.onData(base64);
          }
        } catch (error) {
          this.config.onError(error instanceof Error ? error : new Error("Failed to convert audio data"));
        }
      }
      handleStop() {
        this.cleanup();
      }
      cleanup() {
        this.stream?.getTracks().forEach((track) => track.stop());
        this.stream = null;
        if (this.recorder) {
          this.recorder.onstop = null;
          this.recorder.ondataavailable = null;
          this.recorder.onerror = null;
          this.recorder.removeEventListener("dataavailable", this.handleDataAvailable.bind(this));
          this.recorder.removeEventListener("stop", this.handleStop.bind(this));
          this.recorder.removeEventListener("error", () => {
          });
          this.recorder.removeEventListener("statechange", () => {
          });
          this.recorder = null;
        }
      }
    };
  }
});
var WebSocketTransport;
var init_websocket = __esm({
  "src/voice/transports/websocket.ts"() {
    init_audio_recorder();
    WebSocketTransport = class {
      constructor(callbacks) {
        this.ws = null;
        this.recorder = null;
        this.player = null;
        this.audioDeafened = false;
        this.micMuted = false;
        this.debug = false;
        this.callbacks = callbacks;
      }
      // ── ITransport: connect ──────────────────────────────────────────────
      async connect(config) {
        this.debug = config.debug ?? false;
        const wsUrl = config.roomState.websocketUrl;
        if (!wsUrl) {
          throw new Error("WebSocketTransport: no websocketUrl in roomState");
        }
        if (this.debug) {
          console.debug("\u{1F50C} [WSTransport] Connecting:", wsUrl);
        }
        this.initPlayer();
        this.initRecorder();
        await this.recorder?.start();
        this.ws = new WebSocket(wsUrl);
        this.ws.binaryType = "arraybuffer";
        this.ws.onopen = () => {
          if (this.debug) console.debug("[WSTransport] WS open");
          this.sendTwilioSimulationMessages(config.roomState);
          const start = {
            type: "start",
            input_audio_config: { sampling_rate: 48e3, audio_encoding: "linear16" },
            output_audio_config: { sampling_rate: 24e3, audio_encoding: "linear16" }
          };
          this.sendRaw(start);
        };
        this.ws.onmessage = (event) => {
          this.handleMessage(event);
        };
        this.ws.onerror = () => {
          this.callbacks.onError(new Error("WebSocket error"));
        };
        this.ws.onclose = (event) => {
          if (this.debug) {
            console.debug("\u{1F50C} [WSTransport] WS closed:", { code: event.code, reason: event.reason });
          }
          this.callbacks.onClose(event.code, event.reason);
        };
      }
      // ── ITransport: disconnect ───────────────────────────────────────────
      disconnect(code = 1e3, reason = "client-stop") {
        if (this.ws) {
          this.ws.close(code, reason);
        }
      }
      // ── ITransport: sendMessage ──────────────────────────────────────────
      sendMessage(message) {
        this.sendRaw(message);
      }
      // ── ITransport: isConnected ──────────────────────────────────────────
      isConnected() {
        return this.ws?.readyState === WebSocket.OPEN;
      }
      // ── ITransport: audio controls ───────────────────────────────────────
      muteMic() {
        if (this.micMuted) return;
        this.micMuted = true;
        this.recorder?.stop();
      }
      async unmuteMic() {
        if (!this.micMuted) return;
        this.micMuted = false;
        await this.recorder?.start();
      }
      deafen() {
        if (this.audioDeafened) return;
        this.audioDeafened = true;
        this.player?.volume(0);
      }
      undeafen() {
        if (!this.audioDeafened) return;
        this.audioDeafened = false;
        this.player?.volume(1);
      }
      setVolume(volume) {
        this.player?.volume(volume);
      }
      clearAudioBuffer() {
        if (this.player) {
          this.player.destroy();
          this.player = null;
        }
        this.initPlayer();
      }
      // ── ITransport: destroy ──────────────────────────────────────────────
      destroy() {
        this.recorder?.stop();
        this.recorder = null;
        if (this.player) {
          this.player.destroy();
          this.player = null;
        }
        if (this.ws) {
          this.ws.onopen = null;
          this.ws.onmessage = null;
          this.ws.onerror = null;
          this.ws.onclose = null;
          this.ws = null;
        }
      }
      // ── Private: message handling ────────────────────────────────────────
      handleMessage(event) {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "audio") {
            if (this.player && !this.audioDeafened) {
              try {
                const dataBase64 = msg.data;
                const byteArray = base64js.toByteArray(dataBase64);
                const int16Array = new Int16Array(byteArray.buffer);
                this.player.volume(1);
                this.player.feed(int16Array);
              } catch (e) {
                if (this.debug) {
                  console.error("[WSTransport] Error processing audio:", e);
                }
              }
            }
          }
          this.callbacks.onMessage(msg);
        } catch (error) {
          if (this.debug) {
            console.error("[WSTransport] Error parsing message:", error);
          }
          this.callbacks.onError(
            error instanceof Error ? error : new Error(String(error))
          );
        }
      }
      // ── Private: send raw JSON ───────────────────────────────────────────
      sendRaw(message) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
          if (this.debug) {
            console.warn("[WSTransport] Cannot send: WS not open");
          }
          return;
        }
        this.ws.send(JSON.stringify(message));
      }
      // ── Private: audio init ──────────────────────────────────────────────
      initRecorder() {
        this.recorder = new AudioRecorder({
          onData: (base64) => {
            if (this.audioDeafened) return;
            if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
            this.sendRaw({ type: "audio", data: base64 });
          },
          onError: (error) => this.callbacks.onError(error)
        });
      }
      initPlayer() {
        this.player = new PCMPlayer({
          inputCodec: "Int16",
          channels: 1,
          sampleRate: 24e3,
          flushTime: 175,
          fftSize: 1024
        });
        this.player.volume(this.audioDeafened ? 0 : 1);
      }
      // ── Private: PCC compatibility ───────────────────────────────────────
      sendTwilioSimulationMessages(roomState) {
        const customParameters = roomState.connectionParameters;
        if (!customParameters) {
          if (this.debug) {
            console.error("[WSTransport] No connectionParameters \u2014 cannot init");
          }
          return;
        }
        const roomId = roomState.roomId || Date.now().toString();
        const connectedMessage = {
          event: "connected",
          protocol: "Call",
          version: "1.0.0"
        };
        const startMessage = {
          event: "start",
          sequenceNumber: "1",
          start: {
            accountSid: "AC" + "x".repeat(32),
            streamSid: `MZ${roomId}`,
            callSid: `CA${roomId}`,
            tracks: ["inbound", "outbound"],
            mediaFormat: {
              encoding: "audio/x-mulaw",
              sampleRate: 8e3,
              channels: 1
            },
            customParameters
          },
          streamSid: `MZ${roomId}`
        };
        this.sendRaw(connectedMessage);
        this.sendRaw(startMessage);
        if (this.debug) {
          console.debug("[WSTransport] Sent Twilio simulation messages");
        }
      }
    };
  }
});

// src/voice/transports/webrtc.ts
var WebRTCTransport;
var init_webrtc = __esm({
  "src/voice/transports/webrtc.ts"() {
    WebRTCTransport = class {
      /**
       * @param callbacks — transport event callbacks
       * @param signalingUrl — platform API base URL for signaling relay.
       *   The transport POSTs to `{signalingUrl}/offer/{interactionId}` and
       *   `{signalingUrl}/candidates`.
       */
      constructor(callbacks, signalingUrl) {
        this.pc = null;
        this.dc = null;
        this.stream = null;
        this.audioEl = null;
        this.pingInterval = null;
        this.micMuted = false;
        this.audioDeafened = false;
        this.debug = false;
        this.callbacks = callbacks;
        this.signalingUrl = signalingUrl;
      }
      // ── ITransport: connect ──────────────────────────────────────────────
      async connect(config) {
        this.debug = config.debug ?? false;
        const interactionId = config.roomState.interactionId ?? config.roomState.roomId;
        if (this.debug) {
          console.debug("\u{1F310} [WebRTCTransport] Connecting, interaction:", interactionId);
        }
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
        if (this.debug) console.debug("[WebRTCTransport] Mic acquired");
        this.pc = new RTCPeerConnection({
          iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
        });
        this.stream.getTracks().forEach((t) => this.pc.addTrack(t, this.stream));
        this.pc.addTransceiver("audio", { direction: "recvonly" });
        this.dc = this.pc.createDataChannel("pipecat");
        this.dc.onopen = () => {
          if (this.debug) console.debug("[WebRTCTransport] Data channel open \u2014 ready");
          this.pingInterval = setInterval(() => {
            try {
              this.dc?.send("ping");
            } catch {
            }
          }, 1e3);
          this.callbacks.onReady();
        };
        this.dc.onmessage = (e) => this.handleDataChannelMessage(e);
        this.pc.ontrack = (e) => {
          if (this.debug) console.debug("[WebRTCTransport] Remote track:", e.track.kind);
          if (e.track.kind === "audio") {
            if (!this.audioEl) {
              this.audioEl = document.createElement("audio");
              this.audioEl.autoplay = true;
              this.audioEl.setAttribute("playsinline", "true");
              this.audioEl.style.position = "fixed";
              this.audioEl.style.top = "-9999px";
              document.body.appendChild(this.audioEl);
            }
            this.audioEl.srcObject = e.streams[0];
            this.audioEl.muted = this.audioDeafened;
            this.audioEl.play().catch((err) => {
              if (this.debug) console.debug("[WebRTCTransport] audio.play() rejected:", err);
            });
          }
        };
        const candidates = [];
        this.pc.onicecandidate = (e) => {
          if (e.candidate) {
            candidates.push({
              candidate: e.candidate.candidate,
              sdp_mid: e.candidate.sdpMid,
              sdp_mline_index: e.candidate.sdpMLineIndex
            });
          }
        };
        this.pc.oniceconnectionstatechange = () => {
          if (this.debug) {
            console.debug("[WebRTCTransport] ICE:", this.pc?.iceConnectionState);
          }
          if (this.pc?.iceConnectionState === "failed" || this.pc?.iceConnectionState === "disconnected") {
            this.callbacks.onError(new Error(`ICE ${this.pc.iceConnectionState}`));
          }
        };
        const offer = await this.pc.createOffer();
        await this.pc.setLocalDescription(offer);
        await new Promise((resolve) => {
          if (this.pc.iceGatheringState === "complete") return resolve();
          const onGather = () => {
            if (this.pc?.iceGatheringState === "complete") {
              this.pc.removeEventListener("icegatheringstatechange", onGather);
              resolve();
            }
          };
          this.pc.addEventListener("icegatheringstatechange", onGather);
          setTimeout(resolve, 3e3);
        });
        if (this.debug) {
          console.debug("[WebRTCTransport] ICE candidates:", candidates.length);
        }
        const offerResp = await fetch(`${this.signalingUrl}/offer/${interactionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sdp: this.pc.localDescription.sdp,
            type: this.pc.localDescription.type
          })
        });
        if (!offerResp.ok) {
          const text = await offerResp.text().catch(() => "");
          throw new Error(`WebRTC /offer failed [${offerResp.status}]: ${text}`);
        }
        const answer = await offerResp.json();
        if (this.debug) {
          console.debug("[WebRTCTransport] SDP answer, pc_id:", answer.pc_id);
        }
        await this.pc.setRemoteDescription(
          new RTCSessionDescription({ sdp: answer.sdp, type: answer.type })
        );
        if (candidates.length > 0) {
          await fetch(`${this.signalingUrl}/candidates/${interactionId}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ pc_id: answer.pc_id, candidates })
          });
          if (this.debug) {
            console.debug("[WebRTCTransport] ICE candidates sent");
          }
        }
      }
      // ── ITransport: disconnect ───────────────────────────────────────────
      disconnect(_code, _reason) {
        this.destroy();
        this.callbacks.onClose(_code ?? 1e3, _reason ?? "client-stop");
      }
      // ── ITransport: sendMessage ──────────────────────────────────────────
      sendMessage(message) {
        if (!this.dc || this.dc.readyState !== "open") {
          if (this.debug) {
            console.warn("[WebRTCTransport] Cannot send: data channel not open");
          }
          return;
        }
        if (message.type === "audio") return;
        this.dc.send(JSON.stringify(message));
      }
      // ── ITransport: isConnected ──────────────────────────────────────────
      isConnected() {
        return this.dc?.readyState === "open";
      }
      // ── ITransport: audio controls ───────────────────────────────────────
      muteMic() {
        if (this.micMuted) return;
        this.micMuted = true;
        this.stream?.getAudioTracks().forEach((t) => {
          t.enabled = false;
        });
      }
      async unmuteMic() {
        if (!this.micMuted) return;
        this.micMuted = false;
        this.stream?.getAudioTracks().forEach((t) => {
          t.enabled = true;
        });
      }
      deafen() {
        if (this.audioDeafened) return;
        this.audioDeafened = true;
        if (this.audioEl) this.audioEl.muted = true;
      }
      undeafen() {
        if (!this.audioDeafened) return;
        this.audioDeafened = false;
        if (this.audioEl) this.audioEl.muted = false;
      }
      setVolume(volume) {
        if (this.audioEl) this.audioEl.volume = Math.max(0, Math.min(1, volume));
      }
      clearAudioBuffer() {
      }
      // ── ITransport: destroy ──────────────────────────────────────────────
      destroy() {
        if (this.pingInterval) {
          clearInterval(this.pingInterval);
          this.pingInterval = null;
        }
        if (this.dc) {
          try {
            this.dc.close();
          } catch {
          }
          this.dc = null;
        }
        if (this.pc) {
          this.pc.close();
          this.pc = null;
        }
        if (this.stream) {
          this.stream.getTracks().forEach((t) => t.stop());
          this.stream = null;
        }
        if (this.audioEl) {
          this.audioEl.srcObject = null;
          this.audioEl.remove();
          this.audioEl = null;
        }
      }
      // ── Private: data channel message handling ───────────────────────────
      handleDataChannelMessage(event) {
        const raw = event.data;
        if (raw === "pong") return;
        try {
          const msg = JSON.parse(raw);
          if (msg.label === "rtvi-ai") {
            const converted = this.convertRTVIMessage(msg);
            if (converted) {
              this.callbacks.onMessage(converted);
              return;
            }
          }
          this.callbacks.onMessage(msg);
        } catch {
          if (this.debug) {
            console.debug("[WebRTCTransport] Non-JSON DC message:", String(raw).slice(0, 80));
          }
        }
      }
      /**
       * Convert pipecat RTVI-format data channel messages to Audial's message format.
       *
       * RTVI sends: {label:"rtvi-ai", type:"user-transcription"|"bot-transcription",
       *              data:{text:"...", user_id:"", timestamp:"..."}}
       * Audial expects: {type:"transcript", text:"...", sender:"user"|"assistant", timestamp:123}
       */
      convertRTVIMessage(msg) {
        const type = msg.type;
        const data = msg.data;
        if (type === "user-transcription" || type === "bot-transcription") {
          const sender = type === "user-transcription" ? "user" : "assistant";
          const text = data?.text || "";
          if (!text) return null;
          return {
            type: "transcript",
            text,
            sender,
            timestamp: Date.now()
          };
        }
        return null;
      }
    };
  }
});

// src/voice/transports/registry.ts
function findVoiceProvider(credentialType) {
  return VOICE_PROVIDERS.find((p) => p.credentialType === credentialType);
}
function supportedCredentialTypes() {
  return VOICE_PROVIDERS.map((p) => `${p.credentialType} (${p.id})`).join(", ");
}
var VOICE_PROVIDERS;
var init_registry = __esm({
  "src/voice/transports/registry.ts"() {
    VOICE_PROVIDERS = [
      {
        id: "assemblyai",
        credentialType: "ws-url",
        load: async () => (await import('./assemblyai-QG6PIXPM.mjs')).AssemblyAiTransport
      },
      {
        id: "vapi",
        credentialType: "join-url",
        // Pulls @daily-co/daily-js (~1.1 MB) — loaded ONLY for VAPI agents.
        load: async () => (await import('./vapi-3DMA2VJE.mjs')).VapiTransport
      }
    ];
  }
});

// src/voice/voice-client.ts
var voice_client_exports = {};
__export(voice_client_exports, {
  VoiceClient: () => VoiceClient,
  redactStartPayload: () => redactStartPayload
});
function redactStartPayload(payload) {
  return {
    ...payload,
    user: payload.user ? { externalId: payload.user.externalId } : void 0,
    customer: payload.customer ? "[redacted]" : void 0
  };
}
var _VoiceClient, VoiceClient;
var init_voice_client = __esm({
  "src/voice/voice-client.ts"() {
    init_uimessage_stream_parser();
    init_websocket();
    init_webrtc();
    init_registry();
    _VoiceClient = class _VoiceClient extends EventEmitter {
      constructor(publicKey, config = {}) {
        super();
        this.log = {
          log: console.debug.bind(console),
          info: console.info.bind(console),
          debug: console.debug.bind(console),
          warn: console.warn.bind(console),
          error: console.error.bind(console)
        };
        // =========================
        // 2. Connection State & Status
        // =========================
        this.state = 0 /* Idle */;
        /**
         * Active transport — WebSocket or WebRTC.
         * Created on start(), destroyed on stop().
         */
        this.transport = null;
        /**
         * Which transport is actually in use for the current call.
         */
        this.activeTransportType = null;
        /**
         * Room connection state
         */
        this.roomState = null;
        /**
         * The current node ID
         */
        this.currentNodeId = null;
        // =========================
        // 3. Audio State (tracked by Audial, delegated to transport)
        // =========================
        this.audioDeafened = false;
        this.micMuted = false;
        // =========================
        // 4. Retry & Reconnection Logic
        // =========================
        this.retryPolicy = { maxRetries: 3, backoffMs: 1e3 };
        this.retryCount = 0;
        this.reconnectTimer = null;
        this.connectionTimeoutTimer = null;
        // Per-call-intent Idempotency-Key (IETF draft + Stripe pattern).
        // Generated on user-initiated start, reused across retries within the
        // same intent, cleared on successful connection (call is now
        // "consumed") or on stop (next start is a new intent).
        // Persisted to sessionStorage so a reload in the same tab during a
        // pending connect reuses the same key → server returns the existing
        // queued interaction instead of leaking a new one.
        this.idempotencyKey = null;
        // 2 min
        // =========================
        // 6. Chat Session State
        // =========================
        this.chatState = "idle" /* Idle */;
        this.chatSession = null;
        this.chatMessages = [];
        /**
         * The conversation's runtime grant, minted by boot. Every turn must replay it — the public
         * key names the workspace, never the visitor, so it alone cannot post into a conversation.
         */
        this.chatGrant = null;
        this.publicKey = publicKey;
        this.config = {
          baseUrl: this.getBaseUrl(),
          // Debug is OPT-IN. A true default printed every visitor's identity
          // (user email/name, anonymousId) to the console of every embedding
          // site — enable explicitly via config only.
          debug: false,
          initialMuted: false,
          // autoReconnect defaults to FALSE. The old default of true produced
          // zombie interactions: every retry calls start() → createRoom() which
          // hits POST /interactions/websocket and creates a brand-new
          // interaction. A transient WebRTC blip could leave 10+ "queued"
          // interactions in the DB, none of which ever connected or produced
          // a transcript. Users opting into reconnect must set it explicitly.
          autoReconnect: false,
          ...config
        };
        if (this.config.authToken && !this.config.workspaceId) {
          throw new Error("Audial: workspaceId is required when using authToken");
        }
        if (this.config.debug) {
          const authMode = this.config.authToken ? "jwt" : "publicKey";
          console.debug("\u{1F3A4} Audial SDK initialized:", {
            authMode,
            publicKey: this.publicKey ? this.publicKey.substring(0, 10) + "..." : "(none)",
            baseUrl: this.config.baseUrl,
            transport: this.config.transport ?? "auto"
          });
        }
        this.audioDeafened = this.config.initialMuted;
      }
      // ── Auth helpers (unchanged) ───────────────────────────────────────────
      getAuthHeaders() {
        if (this.config.authToken) {
          const headers = {
            "Authorization": `Bearer ${this.config.authToken}`
          };
          if (this.config.workspaceId) {
            headers["x-workspace-id"] = this.config.workspaceId;
          }
          return headers;
        }
        return {};
      }
      applyPublicKeyAuth(url) {
        if (!this.config.authToken && this.publicKey) {
          url.searchParams.set("publicKey", this.publicKey);
        }
      }
      /** A `/convo/:id{suffix}` URL for the live chat session, public key applied. */
      convoUrl(suffix = "") {
        const url = new URL(
          `${this.getHttpUrl()}/api/v1/convo/${this.chatSession?.interactionId}${suffix}`
        );
        this.applyPublicKeyAuth(url);
        return url;
      }
      /** The turn URL — `/convo/:id/message`, with the agent the conversation is bound to. */
      turnUrl() {
        const url = this.convoUrl("/message");
        if (this.chatSession?.agentId) url.searchParams.set("agentId", this.chatSession.agentId);
        return url;
      }
      /**
       * Session headers — the caller's credential plus the conversation's runtime grant.
       *
       * The grant rides `X-Conversation-Grant`, NOT `Authorization`: in JWT mode `getAuthHeaders()`
       * has just put the platform token there, and writing the grant over it left the request with
       * no caller credential — a 401 before it was ever routed.
       *
       * Omitted rather than sent empty when no grant is held: a blank credential reads as a forged
       * token to the verifier, which answers 403 instead of the 401 that means nothing was sent.
       */
      turnHeaders() {
        const headers = {
          "Content-Type": "application/json",
          ...this.getAuthHeaders()
        };
        if (this.chatGrant) headers["X-Conversation-Grant"] = this.chatGrant;
        return headers;
      }
      start(agentIdOrPayload, overrides) {
        if (this.state !== 0 /* Idle */) {
          throw new Error("Audial: call already active");
        }
        this.currentNodeId = null;
        let payload;
        if (typeof agentIdOrPayload === "string") {
          payload = { agentId: agentIdOrPayload };
        } else {
          payload = agentIdOrPayload;
        }
        this.state = 1 /* CreatingRoom */;
        this.retryCount = 0;
        this.lastCallPayload = payload;
        this.lastOverrides = overrides;
        if (!this.idempotencyKey) {
          this.idempotencyKey = this.restoreOrMintIdempotencyKey();
        }
        if (this.connectionTimeoutTimer) {
          clearTimeout(this.connectionTimeoutTimer);
        }
        const timeoutMs = this.config.connectionTimeoutMs ?? 3e4;
        this.connectionTimeoutTimer = setTimeout(() => {
          this.connectionTimeoutTimer = null;
          this.emit("error", new Error("Connection timeout - voice service did not respond"));
          this.stop(4e3, "connection-timeout");
        }, timeoutMs);
        if (this.config.debug) {
          console.debug("\u{1F3A4} Starting call:", {
            payload: redactStartPayload(payload),
            overrides,
            timeoutMs
          });
        }
        this.createRoomAndConnect(payload, overrides);
      }
      /**
       * Minimal description of a real-time session result returned by a
       * `createSession` function. Mirrors the shape of `RealtimeSessionResult`
       * from `@chanl/sdk` so consumers can pass `sdk.realtime.createSession`'s
       * return value (after unwrapping `{ data }`) directly, without importing
       * that type here (which would create a cross-package dependency).
       *
       * Only the fields needed for wiring are required; everything else is optional
       * so this interface is forward-compatible as new credential types are added.
       */
      /**
       * High-level orchestration method: fetch a provider session via an injected
       * `createSession` function, then wire the credential into `connectProviderSession`.
       *
       * This keeps widget-sdk free of a dependency on `@chanl/sdk`. The consumer
       * (e.g. chanl-admin) injects `sdk.realtime.createSession` as a plain function.
       *
       * Phase 1 only supports `credentialType === 'join-url'` (VAPI Daily rooms).
       * Attempting to start a session with `token` or `ws-url` credentials will
       * throw immediately with a clear "not yet supported" message.
       *
       * @param createSession - Async function that, given an agentId, resolves with
       *   a session result containing `{ provider, credentialType, joinUrl?, ... }`.
       * @param agentId - The agent ID to pass to `createSession`.
       * @param opts.overrides - Optional assistant overrides forwarded to the transport.
       *
       * @throws Error if credentialType is not 'join-url' (Phase 1 only).
       * @throws Error if a call is already active.
       * @throws Error (async, via 'error' event) if the transport connect fails.
       */
      async startProviderSession(createSession, agentId, opts) {
        if (this.state !== 0 /* Idle */) {
          throw new Error("Audial: call already active");
        }
        if (this.config.debug) {
          console.debug("\u{1F3A4} startProviderSession: fetching session for agent", agentId);
        }
        const session = await createSession(agentId);
        if (session.credentialType === "join-url") {
          if (!session.joinUrl) {
            throw new Error(
              'Audial: createSession returned credentialType "join-url" but joinUrl is missing'
            );
          }
          this.connectProviderSession({
            provider: session.provider,
            joinUrl: session.joinUrl,
            overrides: opts?.overrides
          });
          return;
        }
        const provider = findVoiceProvider(session.credentialType);
        if (!provider) {
          throw new Error(
            `Audial: credentialType "${session.credentialType}" is not supported. Supported: ${supportedCredentialTypes()}`
          );
        }
        this.connectViaProvider(provider, this.buildProviderConnectConfig(session, opts));
      }
      /**
       * Normalize a broker `/realtime/session` result into a `TransportConnectConfig`.
       * The fields a provider needs are selected by its `credentialType`; the
       * resulting config is passed verbatim to `ITransport.connect()`.
       */
      buildProviderConnectConfig(session, opts) {
        if (session.credentialType === "join-url") {
          if (!session.joinUrl) {
            throw new Error("Audial: join-url session missing joinUrl");
          }
          return { joinUrl: session.joinUrl, overrides: opts?.overrides, debug: this.config.debug };
        }
        if (session.credentialType === "ws-url") {
          if (!session.wsUrl || !session.token) {
            throw new Error("Audial: ws-url session missing wsUrl/token");
          }
          return {
            wsUrl: session.wsUrl,
            token: session.token,
            sessionConfig: session.config,
            onToolCall: opts?.onToolCall,
            debug: this.config.debug
          };
        }
        throw new Error(
          `Audial: cannot build a connect config for credentialType "${session.credentialType}"`
        );
      }
      /**
       * Connect a provider session by lazy-loading its transport from the registry.
       *
       * This is the single connection path for ALL realtime providers (VAPI,
       * AssemblyAI, future ones). `provider.load()` is a dynamic `import()` — the
       * code-split boundary — so a provider's heavy deps (e.g. VAPI →
       * @daily-co/daily-js) are only fetched here, when an agent actually uses that
       * provider. The public event API (ready/closed/transcript/…) is identical
       * regardless of which transport loads.
       */
      connectViaProvider(provider, connectConfig) {
        if (this.state !== 0 /* Idle */) {
          throw new Error("Audial: call already active");
        }
        this.state = 3 /* Connecting */;
        this.currentNodeId = null;
        if (this.connectionTimeoutTimer) clearTimeout(this.connectionTimeoutTimer);
        const timeoutMs = this.config.connectionTimeoutMs ?? 3e4;
        this.connectionTimeoutTimer = setTimeout(() => {
          this.connectionTimeoutTimer = null;
          this.emit("error", new Error("Connection timeout - provider session did not respond"));
          this.stop(4e3, "connection-timeout");
        }, timeoutMs);
        if (this.config.debug) {
          console.debug(`\u{1F3A4} Connecting ${provider.id} provider session`, { timeoutMs });
        }
        void (async () => {
          try {
            const Transport = await provider.load();
            const callbacks = this.createTransportCallbacks();
            this.transport = new Transport(callbacks);
            this.activeTransportType = provider.id;
            if (this.config.debug) {
              console.debug(`\u{1F680} Using ${provider.id} transport for provider session`);
            }
            await this.transport.connect(connectConfig);
          } catch (error) {
            this.handleConnectionError(error instanceof Error ? error : new Error(String(error)));
          }
        })();
      }
      /**
       * Connect to a provider-managed voice session via a direct join URL.
       *
       * Back-compat surface for callers that already hold a `join-url` credential
       * (e.g. a VAPI Daily.co web-call URL) and pass `{ provider, joinUrl }`
       * directly. Routes through the registry — the transport is lazy-loaded, so
       * nothing here references a provider class statically.
       *
       * The public event API (ready, closed, transcript, speech-start, etc.)
       * is identical to the room-based flow.
       *
       * @throws Error if the provider is unknown / not a join-url provider, or a call is active.
       */
      connectProviderSession({
        provider,
        joinUrl,
        overrides
      }) {
        if (!joinUrl) {
          throw new Error("Audial: joinUrl is required for provider sessions");
        }
        const entry = findVoiceProvider("join-url");
        if (!entry || entry.id !== provider) {
          throw new Error(
            `Audial: unsupported provider "${provider}" for join-url sessions` + (entry ? ` \u2014 expected "${entry.id}"` : "")
          );
        }
        this.connectViaProvider(entry, {
          joinUrl,
          overrides,
          debug: this.config.debug
          // roomState is intentionally omitted — provider sessions bypass room creation
        });
      }
      /**
       * Stop the current call
       */
      stop(code = 1e3, reason = "client-stop") {
        if (this.config.debug) {
          console.debug("\u{1F6D1} Stopping call:", { code, reason });
        }
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
        if (this.connectionTimeoutTimer) {
          clearTimeout(this.connectionTimeoutTimer);
          this.connectionTimeoutTimer = null;
        }
        this.clearIdempotencyKey();
        this.roomState = null;
        if (this.transport) {
          this.transport.disconnect(code, reason);
        } else {
          this.emit("closed", code, reason);
          this.cleanup();
        }
      }
      /**
       * Send a message to the server
       */
      send(message, shouldLog = true) {
        if (!this.transport || !this.transport.isConnected()) {
          if (this.config.debug) {
            console.warn("\u26A0\uFE0F Cannot send message (type: " + message.type + "): not connected");
          }
          return;
        }
        if (message.type === "assistant-overrides" || message.type === "set-context" || message.type === "clear-context") {
          if (this.config.debug) {
            console.warn(`\u26A0\uFE0F Message type '${message.type}' not supported by current backend`);
          }
          return;
        }
        try {
          this.transport.sendMessage(message);
          if (this.config.debug && shouldLog) {
            console.debug("\u{1F4E4} Sent message:", message);
          }
        } catch (error) {
          if (this.config.debug) {
            console.error("\u274C Error sending message:", error);
          }
          this.emit("error", error instanceof Error ? error : new Error(String(error)));
        }
      }
      // ── Audio controls (delegated to transport) ────────────────────────────
      deafen() {
        if (this.audioDeafened) return;
        this.audioDeafened = true;
        this.transport?.deafen();
        this.emit("audio-deafened", true);
      }
      undeafen() {
        if (!this.audioDeafened) return;
        this.audioDeafened = false;
        this.transport?.undeafen();
        this.emit("audio-undeafened", false);
      }
      toggleDeafen() {
        this.audioDeafened ? this.undeafen() : this.deafen();
      }
      setVolume(volume) {
        this.transport?.setVolume(volume);
      }
      muteMic() {
        if (this.micMuted) return;
        this.micMuted = true;
        this.emit("mic-muted");
        this.transport?.muteMic();
      }
      async unmuteMic() {
        if (!this.micMuted) return;
        this.micMuted = false;
        this.emit("mic-unmuted");
        try {
          await this.transport?.unmuteMic();
        } catch (error) {
          this.emit("error", error instanceof Error ? error : new Error(String(error)));
        }
      }
      toggleMicMute() {
        this.micMuted ? this.unmuteMic() : this.muteMic();
      }
      // ── Connection management ──────────────────────────────────────────────
      /**
       * Create room and connect via the appropriate transport.
       */
      async createRoomAndConnect(payload, overrides) {
        try {
          if (this.config.debug) {
            console.debug("\u{1F3E0} Creating websocket room...");
          }
          const room = await this.createRoom(payload);
          this.roomState = {
            roomId: room.id,
            // The WebRTC /offer relay resolves the room by interactionId, not the
            // room's own id. Prefer interactionId; fall back to id for safety.
            interactionId: room.interactionId ?? room.id,
            status: room.status,
            websocketUrl: room.websocketUrl,
            connectionParameters: room.connectionParameters,
            dailyRoom: room.dailyRoom,
            dailyToken: room.dailyToken
          };
          if (room.status === "failed" /* Failed */ || room.status === "expired" /* Expired */) {
            throw new Error(`Room ${room.status}: ${room.id}`);
          }
          const wantsDaily = !!(room.dailyRoom && room.dailyToken);
          if (!wantsDaily && !room.websocketUrl) {
            throw new Error("Room created but no websocket URL available");
          }
          this.state = 3 /* Connecting */;
          const callbacks = this.createTransportCallbacks();
          if (wantsDaily) {
            try {
              const { DailyTransport } = await import('./daily-MZYB2XCS.mjs');
              this.transport = new DailyTransport(callbacks);
              this.activeTransportType = "daily";
              if (this.config.debug) console.debug("\u{1F680} Using daily transport");
              await this.transport.connect({
                roomState: this.roomState,
                overrides,
                debug: this.config.debug
              });
              return;
            } catch (dailyError) {
              if (this.config.debug) {
                console.warn("\u26A0\uFE0F Daily failed, falling back to WebSocket:", dailyError);
              }
              this.transport?.destroy();
              if (!room.websocketUrl) throw dailyError;
              this.transport = new WebSocketTransport(callbacks);
              this.activeTransportType = "websocket";
              await this.transport.connect({
                roomState: this.roomState,
                overrides,
                debug: this.config.debug
              });
              return;
            }
          }
          const transportType = this.resolveTransportType();
          if (transportType === "webrtc") {
            const signalingUrl = this.resolveSignalingUrl();
            this.transport = new WebRTCTransport(callbacks, signalingUrl);
            this.activeTransportType = "webrtc";
          } else {
            this.transport = new WebSocketTransport(callbacks);
            this.activeTransportType = "websocket";
          }
          if (this.config.debug) {
            console.debug(`\u{1F680} Using ${this.activeTransportType} transport`);
          }
          try {
            await this.transport.connect({
              roomState: this.roomState,
              overrides,
              debug: this.config.debug
            });
          } catch (transportError) {
            if (this.activeTransportType === "webrtc") {
              if (this.config.debug) {
                console.warn("\u26A0\uFE0F WebRTC failed, falling back to WebSocket:", transportError);
              }
              this.transport.destroy();
              this.transport = new WebSocketTransport(callbacks);
              this.activeTransportType = "websocket";
              await this.transport.connect({
                roomState: this.roomState,
                overrides,
                debug: this.config.debug
              });
            } else {
              throw transportError;
            }
          }
        } catch (error) {
          this.roomState = null;
          this.handleConnectionError(error instanceof Error ? error : new Error(String(error)));
        }
      }
      /**
       * Determine which transport to use based on config + browser capabilities.
       *
       * Default is 'auto': uses WebRTC if browser supports RTCPeerConnection,
       * otherwise falls back to WebSocket. Signaling goes through the platform's
       * /voice-ws/ proxy — no separate bot URL needed.
       */
      resolveTransportType() {
        const requested = this.config.transport;
        if (requested === "websocket") return "websocket";
        if (requested === "webrtc") return "webrtc";
        return "websocket";
      }
      /**
       * Get the WebRTC signaling relay URL.
       *
       * Points to the platform's bot provider endpoint which relays signaling
       * to the voice-bot. Same URL in dev, staging, prod — no separate bot URL.
       *
       * POST {url}/offer/{interactionId} → platform → voice-bot /offer
       * POST {url}/candidates → platform → voice-bot /candidates
       */
      resolveSignalingUrl() {
        const explicit = this.config.webrtcSignalingUrl;
        if (explicit) return explicit;
        return `${this.getHttpUrl()}/api/v1/bot/call/providers/webrtc`;
      }
      /**
       * Create the callbacks object that wires transport events to Audial.
       */
      createTransportCallbacks() {
        return {
          onReady: () => {
            if (this.connectionTimeoutTimer) {
              clearTimeout(this.connectionTimeoutTimer);
              this.connectionTimeoutTimer = null;
            }
            this.clearIdempotencyKey();
            this.state = 4 /* Active */;
            this.emit("ready");
          },
          onMessage: (msg) => {
            this.handleTransportMessage(msg);
          },
          onError: (error) => {
            if (this.config.debug) {
              console.error("\u274C Transport error:", error);
            }
            this.emit("error", error);
          },
          onClose: (code, reason) => {
            if (this.config.debug) {
              console.debug("\u{1F50C} Transport closed:", { code, reason });
            }
            this.emit("closed", code, reason);
            this.cleanup();
          }
        };
      }
      /**
       * Handle messages from the transport. All event routing happens here.
       * Audio handling is done internally by the transport — we only get
       * the message for event emission purposes.
       */
      handleTransportMessage(msg) {
        this.emit("message", msg);
        switch (msg.type) {
          case "ready": {
            if (this.connectionTimeoutTimer) {
              clearTimeout(this.connectionTimeoutTimer);
              this.connectionTimeoutTimer = null;
            }
            if (this.state !== 4 /* Active */) {
              this.state = 4 /* Active */;
              this.emit("ready");
            }
            if (this.lastOverrides && this.config.debug) {
              console.warn("\u26A0\uFE0F Assistant overrides not supported by current backend");
            }
            break;
          }
          case "speech-start": {
            this.emit("speech-start");
            break;
          }
          case "speech-end": {
            this.emit("speech-end");
            break;
          }
          case "call-end": {
            if (this.config.debug) {
              console.debug("Message call-end received from server");
            }
            this.emit("closed", 1e3, "server-end");
            this.stop();
            break;
          }
          case "error": {
            this.emit("error", new Error(msg?.message));
            break;
          }
          case "transcript": {
            this.emit("transcript", msg);
            break;
          }
          case "audio": {
            break;
          }
          case "node-update": {
            if (this.config.debug) {
              console.debug("[Audial] Node update:", msg.node_id);
            }
            this.currentNodeId = msg.node_id;
            this.emit("node-update", this.currentNodeId);
            break;
          }
          case "interruption": {
            if (this.config.debug) {
              console.debug("[Audial] Interruption received - clearing audio buffer");
            }
            this.transport?.clearAudioBuffer();
            this.emit("interruption");
            break;
          }
        }
      }
      // ── Connection error + retry ──────────────────────────────────────────
      handleConnectionError(error) {
        if (this.config.debug) {
          console.error("\u274C Connection error:", error);
        }
        this.state = 0 /* Idle */;
        this.emit("error", error);
        if (this.config.autoReconnect && this.retryCount < this.retryPolicy.maxRetries) {
          this.retryCount++;
          const delay = this.retryPolicy.backoffMs * Math.pow(2, this.retryCount - 1);
          if (this.config.debug) {
            console.debug(`\u{1F504} Retrying connection in ${delay}ms (attempt ${this.retryCount}/${this.retryPolicy.maxRetries})`);
          }
          const roomToReuse = this.roomState;
          this.reconnectTimer = setTimeout(() => {
            if (roomToReuse) {
              this.retryTransportOnly(roomToReuse);
            } else {
              this.start(this.lastCallPayload, this.lastOverrides);
            }
          }, delay);
        } else {
          this.cleanup();
        }
      }
      /**
       * Reconnect the transport layer without re-creating the platform room.
       * Used by the retry path to avoid leaking a new "queued" interaction per
       * attempt. The existing roomState is preserved; only a fresh transport
       * is constructed and connected to the same room.
       */
      async retryTransportOnly(roomState) {
        try {
          if (this.transport) {
            try {
              this.transport.destroy();
            } catch {
            }
            this.transport = null;
          }
          this.state = 3 /* Connecting */;
          this.roomState = roomState;
          const transportType = this.resolveTransportType();
          const callbacks = this.createTransportCallbacks();
          if (transportType === "webrtc") {
            const signalingUrl = this.resolveSignalingUrl();
            this.transport = new WebRTCTransport(callbacks, signalingUrl);
            this.activeTransportType = "webrtc";
          } else {
            this.transport = new WebSocketTransport(callbacks);
            this.activeTransportType = "websocket";
          }
          await this.transport.connect({
            roomState,
            overrides: this.lastOverrides,
            debug: this.config.debug
          });
        } catch (error) {
          this.handleConnectionError(error instanceof Error ? error : new Error(String(error)));
        }
      }
      reconnect() {
        if (this.lastCallPayload) {
          if (this.config.debug) {
            console.debug("\u{1F504} Reconnecting...");
          }
          this.stop();
          setTimeout(() => {
            this.start(this.lastCallPayload, this.lastOverrides);
          }, 100);
        } else {
          throw new Error("Audial: no previous call to reconnect to");
        }
      }
      // ── Connection status ─────────────────────────────────────────────────
      getConnectionStatus() {
        const baseStatus = {
          connected: this.transport?.isConnected() ?? false,
          state: this.state,
          readyState: this.transport?.isConnected() ? 1 : null,
          retryCount: this.retryCount
        };
        if (this.roomState) {
          baseStatus.roomState = this.roomState;
        }
        return baseStatus;
      }
      // ── Cleanup ───────────────────────────────────────────────────────────
      cleanup() {
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
        if (this.connectionTimeoutTimer) {
          clearTimeout(this.connectionTimeoutTimer);
          this.connectionTimeoutTimer = null;
        }
        this.roomState = null;
        if (this.transport) {
          this.transport.destroy();
          this.transport = null;
        }
        this.activeTransportType = null;
        this.state = 0 /* Idle */;
        this.removeAllListeners();
      }
      // ── Room creation (unchanged) ─────────────────────────────────────────
      getHttpUrl() {
        return this.config.baseUrl.startsWith("http") ? this.config.baseUrl : this.config.baseUrl.includes("localhost") ? `http://${this.config.baseUrl}` : `https://${this.config.baseUrl}`;
      }
      getBaseUrl() {
        if (typeof window !== "undefined" && window.__AUDIAL_CONFIG__) {
          return window.__AUDIAL_CONFIG__.baseUrl;
        }
        if (typeof process !== "undefined") {
          const env = process.env;
          if (env.AUDIAL_BASE_URL) return env.AUDIAL_BASE_URL;
          if (env.NODE_ENV === "production" && env.AUDIAL_PROD_URL) return env.AUDIAL_PROD_URL;
          if (env.NODE_ENV === "staging" && env.AUDIAL_STAGING_URL) return env.AUDIAL_STAGING_URL;
          if (env.NODE_ENV === "development" && env.AUDIAL_DEV_URL) return env.AUDIAL_DEV_URL;
        }
        if (typeof window !== "undefined" && window.location && window.location.hostname) {
          const hostname = window.location.hostname;
          if (hostname === "localhost" || hostname === "127.0.0.1") {
            return "localhost:8000";
          }
          if (hostname.includes("staging")) {
            return "lavoz-api-staging.fly.dev";
          }
          if (hostname.includes("production") || hostname.includes("audial.co") || hostname.includes("audial.ai")) {
            return "lavoz-api.fly.dev";
          }
        }
        return "lavoz-api.fly.dev";
      }
      async createRoom(payload) {
        const baseUrl = `${this.getHttpUrl()}/api/v1/interactions/websocket`;
        const fullPayload = {
          type: "voice",
          provider: "websocket",
          direction: "web",
          // Ask chanl-api to eagerly provision a Daily (managed WebRTC) room and
          // return { dailyRoom, dailyToken }. Only sent when explicitly configured —
          // omitted otherwise, so websocket/webrtc/auto behavior is unchanged.
          transport: this.config.transport === "daily" ? "daily" : void 0,
          agentId: payload.agentId,
          agentSettings: payload.agentSettings ? this.expandAgentSettings(payload.agentSettings) : void 0,
          languageCode: payload.languageCode,
          customerId: payload.customerId,
          // Stable anonymous visitor id → backend resolves anon_<id> Customer, the
          // SAME one chat resolves, so voice + chat unify into one record. Prefer a
          // per-call value, else the client-config value the loader threaded in.
          // Still sent even when a `user` is present — the server IGNORES it in that
          // case (identified user wins), so we must NOT drop it client-side or the
          // pure-anonymous flow breaks.
          anonymousId: payload.anonymousId ?? this.config.anonymousId,
          // Identified end-user + its server-verified hash (bug #46). Threaded here
          // — before transport selection — so identity binds the logged-in Customer
          // regardless of the voice transport (daily / websocket / webrtc all flow
          // through createRoom). Per-call wins over client-config, mirroring the
          // chat-client's merge. Server precedence: trusted customerId > verified
          // user.externalId (userHash checked over externalId) > phone > anon.
          user: payload.user ?? this.config.user,
          userHash: payload.userHash ?? this.config.userHash,
          customer: payload.customer
        };
        const url = new URL(baseUrl);
        this.applyPublicKeyAuth(url);
        if (payload.agentId) {
          url.searchParams.set("agentId", payload.agentId);
        }
        try {
          const response = await fetch(url.toString(), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...this.getAuthHeaders(),
              // IETF Idempotency-Key (draft-ietf-httpapi-idempotency-key-header-07).
              // Lets the server recognize retries of the same call intent and
              // return the existing interaction instead of leaking a new one.
              ...this.idempotencyKey && { "Idempotency-Key": this.idempotencyKey }
            },
            body: JSON.stringify(fullPayload)
          });
          if (!response.ok) {
            let errorMessage;
            try {
              const errorText = await response.text();
              errorMessage = errorText || response.statusText || "Unknown error";
            } catch {
              errorMessage = response.statusText || "Failed to read error response";
            }
            throw new Error(`Room creation failed [${response.status}]: ${errorMessage}`);
          }
          try {
            const result = await response.json();
            const room = result.data?.data || result.data || result;
            if (room.expiresAt instanceof Date) room.expiresAt = room.expiresAt.toISOString();
            if (room.createdAt instanceof Date) room.createdAt = room.createdAt.toISOString();
            if (room.updatedAt instanceof Date) room.updatedAt = room.updatedAt.toISOString();
            return room;
          } catch (parseError) {
            throw new Error(`Room creation succeeded but response is not valid JSON: ${parseError instanceof Error ? parseError.message : "Parse error"}`);
          }
        } catch (error) {
          if (error instanceof Error) {
            if (error.message.includes("Room creation failed")) throw error;
            throw new Error(`Network error during room creation: ${error.message}`);
          }
          throw new Error(`Unexpected error during room creation: ${String(error)}`);
        }
      }
      // ── Idempotency-Key helpers ───────────────────────────────────────────
      /**
       * Mint a new Idempotency-Key, or restore one from sessionStorage if a
       * recent key exists (within IDEMPOTENCY_MAX_AGE_MS). Restore path
       * handles the "user refreshed the tab during a pending connect" case:
       * the original interaction is still queued on the server, and reusing
       * the key gets us back the same interaction instead of leaking a new
       * one.
       */
      restoreOrMintIdempotencyKey() {
        try {
          if (typeof sessionStorage !== "undefined") {
            const cached = sessionStorage.getItem(_VoiceClient.IDEMPOTENCY_STORAGE_KEY);
            const at = Number(sessionStorage.getItem(_VoiceClient.IDEMPOTENCY_STORAGE_AT) || 0);
            if (cached && Date.now() - at < _VoiceClient.IDEMPOTENCY_MAX_AGE_MS) {
              if (this.config.debug) {
                console.debug("\u{1F511} Restored Idempotency-Key from sessionStorage:", cached.slice(0, 8) + "\u2026");
              }
              return cached;
            }
          }
        } catch {
        }
        const fresh = this.mintUuid();
        try {
          if (typeof sessionStorage !== "undefined") {
            sessionStorage.setItem(_VoiceClient.IDEMPOTENCY_STORAGE_KEY, fresh);
            sessionStorage.setItem(_VoiceClient.IDEMPOTENCY_STORAGE_AT, String(Date.now()));
          }
        } catch {
        }
        if (this.config.debug) {
          console.debug("\u{1F511} Minted Idempotency-Key:", fresh.slice(0, 8) + "\u2026");
        }
        return fresh;
      }
      clearIdempotencyKey() {
        this.idempotencyKey = null;
        try {
          if (typeof sessionStorage !== "undefined") {
            sessionStorage.removeItem(_VoiceClient.IDEMPOTENCY_STORAGE_KEY);
            sessionStorage.removeItem(_VoiceClient.IDEMPOTENCY_STORAGE_AT);
          }
        } catch {
        }
      }
      /**
       * Generate a UUID v4. Prefers crypto.randomUUID() (modern browsers +
       * Node 14.17+); falls back to a getRandomValues-backed implementation,
       * and finally Math.random if neither is available (SSR test envs).
       */
      mintUuid() {
        try {
          if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
          }
          if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
            const bytes = new Uint8Array(16);
            crypto.getRandomValues(bytes);
            bytes[6] = bytes[6] & 15 | 64;
            bytes[8] = bytes[8] & 63 | 128;
            const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
            return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
          }
        } catch {
        }
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
      }
      expandAgentSettings(simple) {
        return {
          name: simple.name || "WebSDK Agent",
          languageCode: simple.languageCode || "en",
          prompt: simple.prompt || "You are a helpful assistant.",
          model: {
            provider: simple.model?.provider || (typeof simple.model === "string" ? "openai" : "openai"),
            model: simple.model?.model || (typeof simple.model === "string" ? simple.model : "gpt-4"),
            temperature: simple.model?.temperature ?? 0.7,
            storeCompletions: simple.model?.storeCompletions ?? true
          },
          voice: {
            provider: "elevenlabs",
            voiceId: simple.voiceId || "21m00Tcm4TlvDq8ikWAM",
            stability: 0.6,
            similarityBoost: 0.9
          },
          transcriber: {
            provider: "deepgram",
            model: "nova-2"
          },
          tools: simple.tools || [],
          routings: simple.routings || [],
          maxDurationSeconds: simple.maxDurationSeconds || 600,
          checkHumanPresence: simple.checkHumanPresence ?? true,
          checkHumanPresenceCount: simple.checkHumanPresenceCount || 2,
          allowedIdleTime: simple.allowedIdleTime || 30,
          allowInterruptions: simple.allowInterruptions ?? true,
          interruptionSensitivity: simple.interruptionSensitivity || "high",
          enableCutoffResponses: simple.enableCutoffResponses ?? false,
          cutoffResponses: simple.cutoffResponses || []
        };
      }
      // ═══════════════════════════════════════════════════════════════════════
      // CHAT API METHODS (unchanged — purely HTTP, no transport dependency)
      // ═══════════════════════════════════════════════════════════════════════
      async createChatSession(agentId, options = {}) {
        if (this.chatState !== "idle" /* Idle */ && this.chatState !== "ended" /* Ended */) {
          throw new Error("Chat session already active. End current session first.");
        }
        this.setChatState("creating" /* Creating */);
        const url = new URL(`${this.getHttpUrl()}/api/v1/convo/boot`);
        this.applyPublicKeyAuth(url);
        url.searchParams.set("agentId", agentId);
        try {
          const response = await fetch(url.toString(), {
            method: "POST",
            headers: { "Content-Type": "application/json", ...this.getAuthHeaders() },
            body: JSON.stringify(options)
          });
          if (!response.ok) {
            const errorText = await response.text().catch(() => "Unknown error");
            throw new Error(`Failed to create chat session [${response.status}]: ${errorText}`);
          }
          const result = await response.json();
          const raw = result.data?.data || result.data || result;
          const conversationId = raw.conversationId || raw.interactionId || raw.sessionId;
          const sessionData = { ...raw, conversationId, interactionId: conversationId, sessionId: conversationId };
          this.chatGrant = raw.sessionToken ?? null;
          this.chatSession = {
            sessionId: conversationId,
            interactionId: conversationId,
            agentId: sessionData.agentId,
            agentName: sessionData.agentName,
            model: sessionData.model,
            messages: [],
            createdAt: sessionData.createdAt
          };
          this.chatMessages = [];
          this.setChatState("active" /* Active */);
          this.emit("chat-session-created", sessionData);
          if (this.config.debug) {
            console.debug("\u{1F4AC} Chat session created:", sessionData);
          }
          return sessionData;
        } catch (error) {
          this.setChatState("error" /* Error */);
          this.emit("error", error instanceof Error ? error : new Error(String(error)));
          throw error;
        }
      }
      /** Append a turn to both the flat log and the session's own history. */
      recordTurn(role, content) {
        const entry = { role, content, timestamp: (/* @__PURE__ */ new Date()).toISOString() };
        this.chatMessages.push(entry);
        this.chatSession?.messages.push(entry);
      }
      /** Validate, mark the turn in flight, record the visitor's message. Returns the state to restore on failure. */
      beginTurn(message) {
        if (!this.chatSession) {
          throw new Error("No active chat session. Create a session first.");
        }
        if (this.chatState === "sending" /* Sending */) {
          throw new Error("Message already being sent. Wait for response.");
        }
        if (!message.trim()) {
          throw new Error("Message cannot be empty.");
        }
        const previousState = this.chatState;
        this.setChatState("sending" /* Sending */);
        this.recordTurn("user", message);
        return previousState;
      }
      /** Record the reply, return the session to Active, and announce it. */
      completeTurn(messageData) {
        this.recordTurn("assistant", messageData.message);
        this.setChatState("active" /* Active */);
        this.emit("chat-message", messageData);
      }
      async sendChatMessage(message) {
        const previousState = this.beginTurn(message);
        try {
          const response = await fetch(this.turnUrl().toString(), {
            method: "POST",
            headers: this.turnHeaders(),
            body: JSON.stringify({ message, mode: "sync" })
          });
          if (!response.ok) {
            const errorText = await response.text().catch(() => "Unknown error");
            throw new Error(`Failed to send message [${response.status}]: ${errorText}`);
          }
          const result = await response.json();
          const messageData = result.data?.data || result.data || result;
          this.completeTurn(messageData);
          if (this.config.debug) {
            console.debug("\u{1F4AC} Chat message sent:", { sent: message, received: messageData.message });
          }
          return messageData;
        } catch (error) {
          this.setChatState(previousState);
          this.emit("error", error instanceof Error ? error : new Error(String(error)));
          throw error;
        }
      }
      async streamChatMessage(message, onChunk) {
        const previousState = this.beginTurn(message);
        try {
          const response = await fetch(this.turnUrl().toString(), {
            method: "POST",
            headers: this.turnHeaders(),
            body: JSON.stringify({ message, mode: "stream" })
          });
          if (!response.ok) {
            const errorText = await response.text().catch(() => "Unknown error");
            throw new Error(`Failed to stream message [${response.status}]: ${errorText}`);
          }
          if (!response.body) {
            throw new Error("Response body is not readable (streaming not supported by environment)");
          }
          const { text: fullText } = await parseUIMessageStream(response.body, onChunk);
          const sessionId = this.chatSession?.sessionId || "";
          const messageData = { sessionId, message: fullText };
          this.completeTurn(messageData);
          if (this.config.debug) {
            console.debug("\u{1F4AC} Chat message streamed:", { sent: message, received: fullText.substring(0, 100) + (fullText.length > 100 ? "..." : "") });
          }
          return messageData;
        } catch (error) {
          this.setChatState(previousState);
          this.emit("error", error instanceof Error ? error : new Error(String(error)));
          throw error;
        }
      }
      async getChatHistory() {
        if (!this.chatSession) {
          throw new Error("No active chat session.");
        }
        const url = this.convoUrl("/messages");
        try {
          const response = await fetch(url.toString(), { headers: this.turnHeaders() });
          if (!response.ok) {
            const errorText = await response.text().catch(() => "Unknown error");
            throw new Error(`Failed to get chat history [${response.status}]: ${errorText}`);
          }
          const result = await response.json();
          const raw = result.data?.data || result.data || result;
          const historyData = {
            sessionId: this.chatSession.sessionId,
            messages: raw.messages ?? []
          };
          this.chatMessages = historyData.messages;
          if (this.chatSession) {
            this.chatSession.messages = historyData.messages;
          }
          if (this.config.debug) {
            console.debug("\u{1F4AC} Chat history retrieved:", historyData.messages.length, "messages");
          }
          return historyData;
        } catch (error) {
          this.emit("error", error instanceof Error ? error : new Error(String(error)));
          throw error;
        }
      }
      async endChatSession() {
        if (!this.chatSession) {
          if (this.config.debug) {
            console.debug("\u{1F4AC} No active chat session to end");
          }
          return;
        }
        this.setChatState("ending" /* Ending */);
        const interactionId = this.chatSession.interactionId;
        const url = this.convoUrl("/close");
        try {
          const response = await fetch(url.toString(), {
            method: "POST",
            headers: this.turnHeaders(),
            body: JSON.stringify({})
          });
          if (!response.ok) {
            const errorText = await response.text().catch(() => "Unknown error");
            throw new Error(`Failed to end chat session [${response.status}]: ${errorText}`);
          }
          this.chatSession = null;
          this.chatGrant = null;
          this.chatMessages = [];
          this.setChatState("ended" /* Ended */);
          this.emit("chat-session-ended", interactionId);
          if (this.config.debug) {
            console.debug("\u{1F4AC} Chat session ended:", interactionId);
          }
        } catch (error) {
          this.setChatState("error" /* Error */);
          this.emit("error", error instanceof Error ? error : new Error(String(error)));
          throw error;
        }
      }
      getChatState() {
        return this.chatState;
      }
      getChatSession() {
        return this.chatSession;
      }
      getChatMessages() {
        return [...this.chatMessages];
      }
      hasChatSession() {
        return this.chatSession !== null && this.chatState === "active" /* Active */;
      }
      setChatState(state) {
        if (this.chatState !== state) {
          this.chatState = state;
          this.emit("chat-state-change", state);
        }
      }
      resetChatState() {
        this.chatState = "idle" /* Idle */;
        this.chatSession = null;
        this.chatMessages = [];
      }
    };
    _VoiceClient.IDEMPOTENCY_STORAGE_KEY = "chanl:voice-idem-key";
    _VoiceClient.IDEMPOTENCY_STORAGE_AT = "chanl:voice-idem-at";
    _VoiceClient.IDEMPOTENCY_MAX_AGE_MS = 12e4;
    VoiceClient = _VoiceClient;
  }
});

export { VoiceClient, init_uimessage_stream_parser, init_voice_client, parseUIMessageStream, voice_client_exports };
//# sourceMappingURL=chunk-7H37YIE3.mjs.map
//# sourceMappingURL=chunk-7H37YIE3.mjs.map